import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import TextFieldAtom, { TextFieldMessagesType } from "../../../atoms/textfield";
import {
  LabelPairedArrowLeftToLineSmBoldIcon,
  LabelPairedArrowRightToLineSmBoldIcon,
  LabelPairedCalendarSmBoldIcon,
  LabelPairedChevronDownSmBoldIcon,
  LabelPairedChevronLeftSmBoldIcon,
  LabelPairedChevronRightSmBoldIcon,
  LabelPairedCircleXmarkSmBoldIcon,
} from "@deriv/quill-icons";

import { useClickOutside } from "../../../../src/hooks/useClickOutside";
import DropdownContainerAtom from "../../../../src/atoms/dropdown/container";
import "react-calendar/dist/Calendar.css";
import "../datepicker/Datepicker.css";
import Calendar from "react-calendar";
import { format, parse } from "date-fns";
import { createRoot } from "react-dom/client";
import IconButton from "../../buttons/icon";
import BasicButton from "../../buttons/basic";
import { useDevice } from "../../../hooks/useDevice";
import { ActionSheetType } from "../../action-sheet";

export type DateRangePickerType = {
  className?: string;
  showCharacterCounter?: boolean;
  message?: string;
  indicator?: "neutral" | "fail" | "success";
  state?: "default" | "disabled";
  type?: "outline" | "fill";
  size?: "lg" | "md" | "sm";
  textAlignment?: "left" | "center";
  variant?: "labelled" | "labelless";
  isRequired?: boolean;
  optionalText?: string;
  maxLength?: number;
  value?: [string | number, string | number];
  minDate?: string | Date;
  maxDate?: string | Date;
  onChange?: (value: [string, string]) => void;
  actionSheet?: ActionSheetType;
  dateFormat?:
    | "dd/mm/yyyy"
    | "mm/dd/yyyy"
    | "yyyy/mm/dd"
    | "dd/mm/yy"
    | "mm/dd/yy"
    | "yyyy/mm/yy"
    | "dd-mm-yyyy"
    | "mm-dd-yyyy"
    | "yyyy-mm-dd"
    | "dd-mm-yy"
    | "mm-dd-yy";
  messages?: {
    text: string;
    type: "neutral" | "fail" | "success";
    icon?: ReactNode;
  }[];
  inputStartDate?: {
    label?: string;
    placeholder?: string;
    message?: string,
    messages?: TextFieldMessagesType[];
    indicator?: "neutral" | "fail" | "success";
  };
  inputEndDate?: {
    label?: string;
    placeholder?: string;
    message?: string,
    messages?: TextFieldMessagesType[];
    indicator?: "neutral" | "fail" | "success";
  };
};

export const DateRangePicker: FunctionComponent<DateRangePickerType> = ({
  dateFormat = "dd/mm/yyyy",
  size = "md",
  minDate,
  maxDate,
  value: propValue,
  onChange,
  inputStartDate,
  inputEndDate,
  actionSheet,
  ...props
}) => {
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [isOpen, setIsOpen] = useState(false);

  const [startActiveMonth, setStartActiveMonth] = useState<Date | null>(null);
  const [endActiveMonth, setEndActiveMonth] = useState<Date | null>(null);

  const [activeInput, setActiveInput] = useState<"start" | "end">("start");
  const lastActiveInputRef = useRef<"start" | "end">(activeInput);
  const endInputRef = useRef<HTMLInputElement>(null);
  const startInputRef = useRef<HTMLInputElement>(null);

  const { isMobile } = useDevice();

  // Convert propValue to Date objects if provided
  useEffect(() => {
    if (propValue && Array.isArray(propValue)) {
      const parseToDate = (date?: string | number) => {
        if (!date) return null;
        if (typeof date === "number") {
          const d = new Date(date);
          return isNaN(d.getTime()) ? null : d;
        }
        if (typeof date === "string") {
          const formatString = mapFormatString(dateFormat);
          const parsed = parse(date, formatString, new Date());
          return !isNaN(parsed.getTime()) ? parsed : null;
        }
        return null;
      };
      setRange([parseToDate(propValue[0]), parseToDate(propValue[1])]);
    }
  }, [propValue, dateFormat]);

  const datepickerContainerOffets = {
    sm: 15,
    md: 20,
    lg: 35,
  };

  const mapFormatString = (dateFormat: string) => {
    return dateFormat
      .replace(/yyyy/g, "yyyy")
      .replace(/yy/g, "yy")
      .replace(/mm/g, "MM")
      .replace(/dd/g, "dd");
  };

  const parseToDate = (date?: string | Date) => {
    if (!date) return undefined;
    if (date instanceof Date) return date;
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const handleInputClick = (input: "start" | "end") => {
    setActiveInput(input);
    setIsOpen(true);
  };

  // Combined navigation for moving both calendars
  const handleMonthNavigation = (direction: 1 | -1) => {
    lastActiveInputRef.current = activeInput;
    if (startActiveMonth && endActiveMonth) {
      const newStart = new Date(startActiveMonth);
      newStart.setMonth(newStart.getMonth() + 2 * direction);
      const newEnd = new Date(endActiveMonth);
      newEnd.setMonth(newEnd.getMonth() + 2 * direction);
      setStartActiveMonth(newStart);
      setEndActiveMonth(newEnd);

      const inputToFocus = lastActiveInputRef.current;

      setTimeout(() => {
        setActiveInput(inputToFocus);
      }, 100);

      if (lastActiveInputRef.current === "end") {
        endInputRef.current?.focus();
      } else {
        startInputRef.current?.focus();
      }
    }
  };

  // Custom navigation icons
  useEffect(() => {
    if (isOpen) {
      const calendar = document.querySelector(".quill-ui-next-calendar");
      const setNavIcon = (selector: string, icon: React.ReactNode) => {
        const nav = calendar?.querySelector(selector);
        if (nav) {
          nav.innerHTML = "";
          const container = document.createElement("span");
          nav.appendChild(container);
          createRoot(container).render(icon);
        }
      };
      setNavIcon(
        ".react-calendar__navigation__prev-button",
        <IconButton
          style="tertiary"
          size="sm"
          icon={<LabelPairedChevronLeftSmBoldIcon />}
          color="blackWhite"
        />
      );
      setNavIcon(
        ".react-calendar__navigation__next-button",
        <IconButton
          size="sm"
          style="tertiary"
          icon={<LabelPairedChevronRightSmBoldIcon />}
          color="blackWhite"
        />
      );
    }
  }, [isOpen]);

  useClickOutside(
    startInputRef,
    () => {
      setIsOpen(false);
    },
    ["quill-ui-next-calendar-daterange-container", "clear-btn"]
  );

  // Display value as 'start - end'
  const displayValue =
    range[0] && range[1]
      ? `${format(range[0], mapFormatString(dateFormat))} - ${format(
          range[1],
          mapFormatString(dateFormat)
        )}`
      : "";

  useEffect(() => {
    if (isOpen) {
      // If not set, initialize to consecutive months
      if (!startActiveMonth) {
        const start = range[0] || new Date();
        setStartActiveMonth(new Date(start.getFullYear(), start.getMonth(), 1));
      }
      if (!endActiveMonth) {
        const start = range[0] || new Date();
        const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
        setEndActiveMonth(end);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Helper to check if a date is in the selected range
  const isInRange = (date: Date) => {
    if (range[0] && range[1] && !isSameDay(range[0], range[1])) {
      const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const start = new Date(
        range[0].getFullYear(),
        range[0].getMonth(),
        range[0].getDate()
      );
      const end = new Date(
        range[1].getFullYear(),
        range[1].getMonth(),
        range[1].getDate()
      );
      return d > start && d < end;
    }
    return false;
  };

  // Helper to check if two dates are the same day
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const getRangeTileClassName = (date: Date, view: string) => {
    if (view !== "month") return undefined;
    if (
      range[0] &&
      range[1] &&
      isSameDay(range[0], range[1]) &&
      isSameDay(date, range[0])
    ) {
      // Both start and end are the same day, and this is that day
      return "quill-ui-next-calendar-range-single";
    }
    if (range[0] && isSameDay(date, range[0])) {
      return "quill-ui-next-calendar-range-start";
    }
    if (range[1] && isSameDay(date, range[1])) {
      return "quill-ui-next-calendar-range-end";
    }
    if (isInRange(date)) {
      return "quill-ui-next-calendar-in-range";
    }
    return undefined;
  };

  // Update handleCalendarChange to accept both single and range values
  const handleCalendarChange = (
    value: Date | Date[] | [Date | null, Date | null] | null
  ) => {
    let date: Date | null = null;
    if (Array.isArray(value)) {
      // Could be [Date, Date] or [Date | null, Date | null]
      date = value[0] instanceof Date ? value[0] : null;
    } else if (value instanceof Date) {
      date = value;
    }
    if (!date) return;

    if (activeInput === "start") {
      let nextEnd = range[1];
      if (range[1] && date > range[1]) {
        nextEnd = null;
      }
      setRange([date, nextEnd]);
      setActiveInput("end");

      startInputRef.current?.click();
      startInputRef.current?.focus();

      setTimeout(() => {
        endInputRef.current?.click();
        endInputRef.current?.focus();
      }, 100);
      return;
    }

    if (activeInput === "end") {
      if (!range[0]) {
        setRange([date, null]);
        setActiveInput("end");
        setTimeout(() => {
          endInputRef.current?.focus();
        }, 0);
        return;
      }
      if (range[0] && !range[1]) {
        if (date < range[0]) {
          setRange([date, null]);
          setTimeout(() => {
            setActiveInput("end");
            endInputRef.current?.focus();
          }, 0);
          return;
        }
        setRange([range[0], date]);
        const formatString = mapFormatString(dateFormat);
        const startStr = format(range[0], formatString);
        const endStr = format(date, formatString);
        onChange?.([startStr, endStr]);
        if (!isMobile) setTimeout(() => setIsOpen(false), 100);
        setTimeout(() => {
          endInputRef.current?.focus();
        }, 0);
        return;
      }
      // fallback: both dates set, just update end
      if (date < range[0]) {
        setRange([date, null]);
        setTimeout(() => {
          setActiveInput("end");
          endInputRef.current?.focus();
        }, 0);
        return;
      }
      setRange([range[0], date]);
      const formatString = mapFormatString(dateFormat);
      const startStr = format(range[0], formatString);
      const endStr = format(date, formatString);
      onChange?.([startStr, endStr]);
      if (!isMobile) setTimeout(() => setIsOpen(false), 100);
      setTimeout(() => {
        endInputRef.current?.focus();
      }, 0);
      return;
    }
  };

  // Mobile-specific calendar change handler
  const handleMobileCalendarChange = (value: Date | (Date | null)[] | null) => {
    if (Array.isArray(value) && value[0] instanceof Date && value[1] instanceof Date) {
      setRange([value[0], value[1]]);
      const formatString = mapFormatString(dateFormat);
      const startStr = format(value[0], formatString);
      const endStr = format(value[1], formatString);
      onChange?.([startStr, endStr]);
    } else if (value instanceof Date) {
      setRange([value, null]);
    } else if (!value) {
      setRange([null, null]);
    }
  };

  const renderDateRangePicker = () => {
    return (
      <div className="quill-ui-next-calendar-daterange-container">
        <IconButton
          className="quill-ui-next-calendar-daterange-prev-button quill-ui-next-calendar-daterange-button"
          style="tertiary"
          size="sm"
          icon={<LabelPairedChevronLeftSmBoldIcon />}
          color="blackWhite"
          onClick={() => handleMonthNavigation(-1)}
        />

        <div className="quill-ui-next-daterange-picker-calendar-box">
          <Calendar
            value={range[0]}
            onChange={(value) => handleCalendarChange(value as Date)}
            locale="en-GB"
            showNeighboringMonth={false}
            className="quill-ui-next-calendar"
            minDate={parseToDate(minDate)}
            maxDate={parseToDate(maxDate)}
            onActiveStartDateChange={({ activeStartDate }) => {
              setStartActiveMonth(activeStartDate);
              // If end calendar is showing the same month, move it forward
              if (
                endActiveMonth &&
                activeStartDate &&
                endActiveMonth.getFullYear() ===
                  activeStartDate.getFullYear() &&
                endActiveMonth.getMonth() === activeStartDate.getMonth()
              ) {
                const nextMonth = new Date(activeStartDate);
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                setEndActiveMonth(nextMonth);
              }
            }}
            activeStartDate={startActiveMonth || undefined}
            navigationLabel={undefined}
            prevLabel={null}
            nextLabel={null}
            minDetail="month"
            maxDetail="month"
            tileClassName={({ date, view }) =>
              getRangeTileClassName(date, view)
            }
          />

          <Calendar
            value={range[1]}
            onChange={handleCalendarChange}
            locale="en-GB"
            showNeighboringMonth={false}
            className="quill-ui-next-calendar"
            minDate={parseToDate(minDate)}
            maxDate={parseToDate(maxDate)}
            activeStartDate={endActiveMonth || undefined}
            navigationLabel={undefined}
            prevLabel={null}
            nextLabel={null}
            minDetail="month"
            maxDetail="month"
            onActiveStartDateChange={({ activeStartDate }) =>
              setEndActiveMonth(activeStartDate)
            }
            tileClassName={({ date, view }) =>
              getRangeTileClassName(date, view)
            }
          />
        </div>
        <div className="quill-ui-next-daterange-picker-calendar-buttons">
          <BasicButton
            style="secondary"
            size="lg"
            label="Today"
            color="blackWhite"
            iconLeft={<LabelPairedArrowLeftToLineSmBoldIcon />}
            onClick={showCurrentAndNextMonth}
          />
        </div>

        <IconButton
          className="quill-ui-next-calendar-daterange-next-button quill-ui-next-calendar-daterange-button"
          style="tertiary"
          size="sm"
          icon={<LabelPairedChevronRightSmBoldIcon />}
          color="blackWhite"
          onClick={() => handleMonthNavigation(1)}
        />
      </div>
    );
  };

  const startValue = range[0]
    ? format(range[0], mapFormatString(dateFormat))
    : "";
  const endValue = range[1]
    ? format(range[1], mapFormatString(dateFormat))
    : "";

  const clearEndDate = () => setRange([range[0], null]);

  // Clear range
  const clearRange = () => {
    setRange([null, null]);
    onChange?.(["", ""]);
  };

  const showCurrentAndNextMonth = () => {
    const now = new Date();
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    setStartActiveMonth(startMonth);
    setEndActiveMonth(endMonth);
  };

  useEffect(() => {
    lastActiveInputRef.current = activeInput;
  }, [activeInput]);

  // Mobile render: one input, one calendar
  const renderMobileDateRangePicker = () => {
    return (
      <div className="quill-ui-next-calendar-daterange-container">
        <Calendar
          selectRange
          value={range[0] && range[1] ? [range[0], range[1]] : range[0]}
          onChange={handleMobileCalendarChange}
          locale="en-GB"
          showNeighboringMonth={false}
          className="quill-ui-next-calendar"
          minDate={parseToDate(minDate)}
          maxDate={parseToDate(maxDate)}
          navigationLabel={undefined}
          prevLabel={
            <IconButton
              style="tertiary"
              size="sm"
              icon={<LabelPairedChevronLeftSmBoldIcon />}
              color="blackWhite"
            />
          }
          nextLabel={
            <IconButton
              size="sm"
              style="tertiary"
              icon={<LabelPairedChevronRightSmBoldIcon />}
              color="blackWhite"
            />
          }
          minDetail="decade"
          maxDetail="month"
          tileClassName={({ date, view }) => getRangeTileClassName(date, view)}
        />
      </div>
    );
  };

  return (
    <div className="quill-ui-next-daterange-picker-input-container">
      {isMobile ? (
        <TextFieldAtom
          {...props}
          size={size}
          allowTyping={false}
          label={`${inputStartDate?.label || "From"} - ${
            inputEndDate?.label || "To"
          }`}
          placeholder={dateFormat}
          iconLeft={<LabelPairedCalendarSmBoldIcon />}
          iconRight={
            <LabelPairedChevronDownSmBoldIcon
              className={`quill-ui-next-rotatable ${
                isOpen && "quill-ui-next-rotate"
              }`}
            />
          }
          iconExtra={
            isOpen && (range[0] || range[1]) ? (
              <LabelPairedCircleXmarkSmBoldIcon
                className="clear-btn"
                onClick={clearRange}
              />
            ) : undefined
          }
          onChange={() => {}}
          onClick={() => setIsOpen(true)}
          value={displayValue}
          className={`${props.state !== "disabled" && "quill-ui-next-pointer"}`}
          customContent={
            <DropdownContainerAtom
              width="fit-content"
              textAlignment={props.textAlignment}
              className="quill-ui-next-daterange-picker-dropdown"
              parent={startInputRef}
              isOpen={isOpen}
              offsetTop={
                props.variant === "labelless"
                  ? datepickerContainerOffets[size]
                  : 36
              }
              onActionSheetClose={() => setIsOpen(false)}
              actionSheet={{
                ...actionSheet,
                buttonPrimary: {
                  label: actionSheet?.buttonPrimary?.label || "Done",
                  onClick: () => {
                    setTimeout(() => {
                      setIsOpen(false);
                      actionSheet?.onClose?.();
                    }, 100);
                  },
                },
              }}
              customContent={renderMobileDateRangePicker()}
            />
          }
        />
      ) : (
        <>
          <TextFieldAtom
            ref={startInputRef as unknown as React.Ref<HTMLInputElement>}
            {...props}
            size={size}
            allowTyping={false}
            label={inputStartDate?.label || "From"}
            placeholder={inputStartDate?.placeholder || dateFormat}
            iconLeft={<LabelPairedArrowLeftToLineSmBoldIcon />}
            iconRight={
              <LabelPairedChevronDownSmBoldIcon
                className={`quill-ui-next-rotatable ${
                  isOpen && "quill-ui-next-rotate"
                }`}
              />
            }
            iconExtra={
              isOpen && range[0] ? (
                <LabelPairedCircleXmarkSmBoldIcon
                  className="clear-btn"
                  onClick={clearRange}
                />
              ) : undefined
            }
            messages={inputStartDate?.messages}
            message={inputStartDate?.message}
            indicator={inputStartDate?.indicator}
            onChange={() => {}}
            onClick={() => handleInputClick("start")}
            value={startValue}
            className={`${
              props.state !== "disabled" && "quill-ui-next-pointer"
            }`}
            customContent={
              <DropdownContainerAtom
                width="fit-content"
                textAlignment={props.textAlignment}
                className="quill-ui-next-daterange-picker-dropdown"
                parent={startInputRef}
                isOpen={isOpen}
                offsetTop={
                  props.variant === "labelless"
                    ? datepickerContainerOffets[size]
                    : 36
                }
                onActionSheetClose={() => setIsOpen(false)}
                customContent={renderDateRangePicker()}
              />
            }
          />
          <TextFieldAtom
            ref={endInputRef as unknown as React.Ref<HTMLInputElement>}
            {...props}
            size={size}
            allowTyping={false}
            label={inputEndDate?.label || "To"}
            placeholder={inputEndDate?.placeholder || dateFormat}
            iconLeft={<LabelPairedArrowRightToLineSmBoldIcon />}
            iconRight={
              <LabelPairedChevronDownSmBoldIcon
                className={`quill-ui-next-rotatable ${
                  isOpen && "quill-ui-next-rotate"
                }`}
              />
            }
            iconExtra={
              isOpen && range[1] ? (
                <LabelPairedCircleXmarkSmBoldIcon
                  className="clear-btn"
                  onClick={clearEndDate}
                />
              ) : undefined
            }
            messages={inputEndDate?.messages}
            message={inputEndDate?.message}
            indicator={inputEndDate?.indicator}
            onChange={() => {}}
            onClick={() => handleInputClick("end")}
            value={endValue}
            className={`${
              props.state !== "disabled" && "quill-ui-next-pointer"
            }`}
          />
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
